// Cave84 — Critic Scores hooks (Phase 2 Week 7)

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query';
import type { CriticScore } from '@cave84/shared-types';
import {
  addCriticScore,
  deleteCriticScore,
  listCriticScoresByVintage,
  listTop3ScoresByVintage,
  type CriticScoreCreateInput
} from '@/services/criticScores';
import { qk } from '@/services/firestore';
import { useAuthStore } from '@/stores/auth.store';

function assertOwner() {
  const role = useAuthStore.getState().userDoc?.role;
  if (role !== 'owner') throw new Error('Cave84: owner role required');
}

export function useCriticScoresByVintage(vintageId: string | undefined): UseQueryResult<CriticScore[]> {
  return useQuery({
    queryKey: vintageId ? qk.criticScores.byVintage(vintageId) : qk.criticScores.byVintage('__noop__'),
    queryFn: () => vintageId ? listCriticScoresByVintage(vintageId) : Promise.resolve([]),
    enabled: !!vintageId,
  });
}

export function useTop3ScoresByVintage(vintageId: string | undefined): UseQueryResult<CriticScore[]> {
  return useQuery({
    queryKey: vintageId
      ? [...qk.criticScores.byVintage(vintageId), 'top3']
      : [...qk.criticScores.byVintage('__noop__'), 'top3'],
    queryFn: () => vintageId ? listTop3ScoresByVintage(vintageId) : Promise.resolve([]),
    enabled: !!vintageId,
  });
}

interface AddScoreInput { vintageId: string; score: CriticScoreCreateInput }

export function useAddCriticScore(): UseMutationResult<string, Error, AddScoreInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vintageId, score }: AddScoreInput) => {
      assertOwner();
      return addCriticScore(vintageId, score);
    },
    onSuccess: (_id, { vintageId }) => {
      void qc.invalidateQueries({ queryKey: qk.criticScores.byVintage(vintageId) });
    },
  });
}

interface DeleteScoreInput { scoreId: string; vintageId: string }

export function useDeleteCriticScore(): UseMutationResult<void, Error, DeleteScoreInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scoreId }: DeleteScoreInput) => {
      assertOwner();
      return deleteCriticScore(scoreId);
    },
    onSuccess: (_v, { vintageId }) => {
      void qc.invalidateQueries({ queryKey: qk.criticScores.byVintage(vintageId) });
    },
  });
}
